import React from "react";
import { motion } from "framer-motion";
import { cn } from "../../../../lib/utils";
import { getThemeClass } from "../../../../lib/theme";

const EnhancedHeroSection = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  return (
    <section
      className={cn(
        "relative min-h-screen flex items-center justify-center pt-16",
        getThemeClass("gradients.hero")
      )}
    >
      {/* Dot matrix overlay */}
      <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMSIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]" />

      <div className="container mx-auto px-6 py-24 flex justify-center">
        <div className="w-full max-w-5xl">
          <div>
            <motion.div
              initial="initial"
              animate="animate"
              className="space-y-12"
            >
              {/* Title */}
              <motion.div variants={fadeIn}>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 text-center">
                  Principal Member of Technical Staff
                  <span
                    className={cn(
                      "block mt-2",
                      getThemeClass("colors.secondary.light", "text")
                    )}
                  >
                    Technical Innovation Architect
                  </span>
                </h1>
              </motion.div>

              {/* Vision Statement */}
              <motion.div
                variants={fadeIn}
                className="mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20"
              >
                <div className="space-y-6">
                  <p className="text-lg md:text-xl text-white leading-relaxed">
                    I build platforms for complex, high-scale problems across edge
                    computing, distributed systems, subscription architecture,
                    and AI. My focus is on systems whose technical decisions can
                    be tied to measurable product and business outcomes.
                  </p>

                  <p className="text-lg md:text-xl text-white leading-relaxed">
                    My work spans Fortune 500 digital transformations, NIH-funded
                    academic research, and patented innovations that have shaped
                    how enterprises deliver content at global scale.
                  </p>

                  <p className="text-lg md:text-xl text-white leading-relaxed">
                    That work includes subscription platforms that enabled
                    business model transitions, event processing that reduced
                    indexing latency from 15 minutes to 5 milliseconds, and AI
                    systems for metadata discovery and quality analysis.
                  </p>

                  <p
                    className={cn(
                      "text-lg md:text-xl leading-relaxed  text-white"
                    )}
                  >
                    My current focus is edge computing, AI platform architecture,
                    and agentic systems that can move from internal tooling into
                    Salesforce products.
                  </p>
                </div>
              </motion.div>

              {/* Key Innovation Areas */}
              <motion.div variants={fadeIn}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    {
                      title: "Content Delivery & Edge Computing",
                      metric: "Global",
                      description:
                        "CDN and edge platform architecture",
                    },
                    {
                      title: "Platform Architecture",
                      metric: "Full-Stack",
                      description:
                        "Multi-platform subscription system with integrated global payments & billing",
                    },
                    {
                      title: "Distributed Systems",
                      metric: "5ms",
                      description:
                        "Indexing latency reduced from 15 minutes",
                    },
                  ].map((area, index) => (
                    <div
                      key={index}
                      className={cn(
                        "bg-white/10",
                        "backdrop-blur-sm",
                        "rounded-lg",
                        "p-6",
                        "border",
                        "border-white/20",
                        "transition-colors",
                        "duration-300",
                        getThemeClass("colors.secondary.light", "borderHover")
                      )}
                    >
                      <motion.div variants={fadeIn}>
                        <h3 className="text-lg font-semibold text-white mb-2">
                          {area.title}
                        </h3>
                        <p
                          className={cn(
                            "text-3xl font-bold",
                            getThemeClass("colors.secondary.light", "text")
                          )}
                        >
                          {area.metric}
                        </p>
                        <p className="text-white/80">{area.description}</p>
                      </motion.div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnhancedHeroSection;
